<script setup lang="ts">
import { groupedActionOptions } from '../../model/inspectorOptions'
import { useFlowEditorContext } from '../../composables/useFlowEditor'
import type { FlowNode } from '../../model/types'

/** Action picker plus options of card, checkbox, audio and timing actions. */
defineProps<{ selectedNode: FlowNode }>()

const editor = useFlowEditorContext()
const { debounced: di, flush: db } = editor.inspector
const {
  updateAction,
  updatePullDirection,
  updatePullAnimation,
  updateCheckboxCount,
  updateCheckboxDelayMs,
  updateAudioElementIndex,
  updateWaitForAudio,
  updateWaitDelayMs,
  updateDelayMs,
} = editor.inspector
</script>

<template>
  <label v-if="selectedNode.type === 'action' && !selectedNode.data.actionType">
    <span>{{ $t('flow.action') }}</span>
    <select
      :value="selectedNode.data.actionType"
      @change="updateAction(($event.target as HTMLSelectElement).value)"
    >
      <optgroup v-for="group in groupedActionOptions" :key="group.label" :label="group.label">
        <option v-for="opt in group.options" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </optgroup>
    </select>
  </label>

  <!-- pull-next-card sub-settings -->
  <template
    v-if="selectedNode.type === 'action' && selectedNode.data.actionType === 'pull-next-card'"
  >
    <label>
      <span>{{ $t('flow.cardSide') }}</span>
      <select
        :value="selectedNode.data.pullDirection ?? 'front'"
        @change="updatePullDirection(($event.target as HTMLSelectElement).value)"
      >
        <option value="front">{{ $t('flow.frontFirst') }}</option>
        <option value="back">{{ $t('flow.backFirst') }}</option>
      </select>
    </label>
    <label>
      <span>{{ $t('flow.animation') }}</span>
      <select
        :value="selectedNode.data.pullAnimation ?? 'default'"
        @change="updatePullAnimation(($event.target as HTMLSelectElement).value)"
      >
        <option value="default">{{ $t('flow.followDeckSetting') }}</option>
        <option value="none">{{ $t('flow.animNone') }}</option>
        <option value="slide-left">{{ $t('flow.slideLeft') }}</option>
        <option value="slide-right">{{ $t('flow.slideRight') }}</option>
        <option value="flip">{{ $t('flow.flip') }}</option>
        <option value="fade">{{ $t('flow.fade') }}</option>
        <option value="swipe-up">{{ $t('flow.swipeUp') }}</option>
        <option value="swipe-down">{{ $t('flow.swipeDown') }}</option>
        <option value="reveal-under">{{ $t('flow.revealUnder') }}</option>
        <option value="scale">{{ $t('flow.scaleAnim') }}</option>
        <option value="confetti">{{ $t('flow.animConfetti') }}</option>
      </select>
    </label>
  </template>

  <!-- set-initial-checkboxes sub-settings -->
  <template
    v-if="
      selectedNode.type === 'action' && selectedNode.data.actionType === 'set-initial-checkboxes'
    "
  >
    <label>
      <span>Initially checked</span>
      <input
        :value="selectedNode.data.checkboxCount ?? 0"
        type="number"
        min="0"
        step="1"
        @input="di(updateCheckboxCount)($event)"
        @blur="db(updateCheckboxCount)($event)"
      />
    </label>
  </template>

  <!-- click-checkbox / unclick-checkbox sub-settings -->
  <template
    v-if="
      selectedNode.type === 'action' &&
      (selectedNode.data.actionType === 'click-checkbox' ||
        selectedNode.data.actionType === 'unclick-checkbox')
    "
  >
    <label>
      <span>{{ $t('flow.countN') }}</span>
      <input
        :value="selectedNode.data.checkboxCount ?? 1"
        type="number"
        min="1"
        step="1"
        @input="di(updateCheckboxCount)($event)"
        @blur="db(updateCheckboxCount)($event)"
      />
    </label>
    <label>
      <span>{{ $t('flow.delayMs') }}</span>
      <input
        :value="selectedNode.data.checkboxDelayMs ?? 500"
        type="number"
        min="0"
        step="100"
        @input="di(updateCheckboxDelayMs)($event)"
        @blur="db(updateCheckboxDelayMs)($event)"
      />
    </label>
  </template>

  <!-- play-audio sub-settings -->
  <label v-if="selectedNode.type === 'action' && selectedNode.data.actionType === 'play-audio'">
    <span>{{ $t('flow.audioElementIndex') }}</span>
    <input
      :value="selectedNode.data.audioElementIndex ?? 0"
      type="number"
      min="0"
      step="1"
      @input="di(updateAudioElementIndex)($event)"
      @blur="db(updateAudioElementIndex)($event)"
    />
  </label>
  <label
    v-if="selectedNode.type === 'action' && selectedNode.data.actionType === 'play-audio'"
    class="flow-settings__checkbox-label"
  >
    <input
      type="checkbox"
      :checked="selectedNode.data.waitForAudio !== false"
      @change="updateWaitForAudio(($event.target as HTMLInputElement).checked)"
    />
    <span>{{ $t('flow.waitForAudio') }}</span>
  </label>

  <!-- wait-for-user-action sub-settings -->
  <label
    v-if="selectedNode.type === 'action' && selectedNode.data.actionType === 'wait-for-user-action'"
  >
    <span>{{ $t('flow.delayAfterClickMs') }}</span>
    <input
      :value="selectedNode.data.waitDelayMs ?? 0"
      type="number"
      min="0"
      step="100"
      @input="di(updateWaitDelayMs)($event)"
      @blur="db(updateWaitDelayMs)($event)"
    />
  </label>

  <label v-if="selectedNode.type === 'action' && selectedNode.data.actionType === 'delay'">
    <span>{{ $t('flow.delayDurationMs') }}</span>
    <input
      :value="selectedNode.data.delayMs ?? 1000"
      type="number"
      min="0"
      step="100"
      @input="di(updateDelayMs)($event)"
      @blur="db(updateDelayMs)($event)"
    />
  </label>
</template>

<style scoped src="../styles/inspectorForm.css"></style>
